/* Card Wrapper - contains rank + card */
.cardWrapper {
  position: relative;
  display: flex;
  align-items: stretch;
  min-height: 200px;
  min-width: 0;
}

/* Big rank number — Axiforma Bold 96px, fades text→transparent.
   The gradient is built from --text-primary so it flips with the theme:
   light ink on dark, dark ink on light. */
.rankBg {
  position: absolute;
  left: 0;
  width: 70px;
  top: 50%;
  transform: translateY(-50%);
  font-family: var(--font-sans);
  font-size: 96px;
  font-weight: 700;
  background: linear-gradient(
    to right,
    color-mix(in srgb, var(--text-primary) 26%, transparent) 19%,
    transparent 105%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1;
  text-align: center;
  pointer-events: none;
  z-index: 0;
  user-select: none;
  white-space: nowrap;
  transition:
    filter var(--duration-base) var(--ease-default),
    -webkit-text-stroke-color var(--duration-base) var(--ease-default);
}

/* On card hover the rank lights up: the fill drops away to an outline-only
   numeral stroked in the same hover gold, with the card's glow echoed around
   it. `:has()` lets the (earlier-in-DOM) rank react to its sibling card.
   drop-shadow (not text-shadow) so the glow follows the stroked outline even
   though the glyph fill is transparent. */
.cardWrapper:has(.card:hover) .rankBg {
  background: none;
  -webkit-text-fill-color: transparent;
  -webkit-text-stroke: 2px var(--accent-secondary);
  color: transparent;
  filter: drop-shadow(0 0 16px rgba(240, 180, 41, 0.5));
}

/* Card — tokenized surface + border, matching location-intelligence's ProjectCard */
.card {
  position: relative;
  display: flex;
  gap: var(--space-4);
  padding: var(--space-4);
  margin-left: 70px;
  flex: 1;
  min-width: 0;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: 0 10px 10px rgba(0, 0, 0, 0.08);
  text-decoration: none;
  overflow: hidden;
  transition: transform var(--duration-fast) var(--ease-default),
    box-shadow var(--duration-fast) var(--ease-default);
  z-index: 1;
}

/* Left amber stripe — fades in on hover (HUD accent, clipped by card radius). */
.card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  pointer-events: none;
  z-index: 5;
  background: linear-gradient(
    180deg,
    transparent 0%,
    var(--accent-secondary) 20%,
    var(--accent-secondary) 80%,
    transparent 100%
  );
  opacity: 0;
  transition: opacity var(--duration-fast) var(--ease-default);
}

/* Right-fading amber wash — fades in on hover. */
.card::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 4;
  background: linear-gradient(to right, rgba(240, 180, 41, 0.08) 0px, transparent 120px);
  opacity: 0;
  transition: opacity var(--duration-fast) var(--ease-default);
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 -8px 30px rgba(240, 180, 41, 0.12);
}

.card:hover::before,
.card:hover::after {
  opacity: 1;
}

/* Blurred poster behind the content — same image as the poster, softened and
   dimmed. The gradient over it keeps the title and revenue readable. */
.splashBg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  opacity: 0.22;
  filter: blur(14px);
  transform: scale(1.1); /* hide the blur's soft edges */
  z-index: 0;
  pointer-events: none;
  transition: opacity var(--duration-fast) var(--ease-default);
}

.splashGrad {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: linear-gradient(135deg, var(--bg-card) 35%, transparent 75%),
    linear-gradient(to top, var(--bg-card) 0%, transparent 55%);
}

.card:hover .splashBg {
  opacity: 0.3;
}

/* Poster — drop-shadow, 5px radius */
.poster {
  position: relative;
  flex-shrink: 0;
  width: 120px;
  height: 182px;
  border-radius: 5px;
  overflow: hidden;
  z-index: 2;
  background: var(--bg-elevated);
  filter: drop-shadow(4px 4px 5px rgba(0, 0, 0, 0.25));
}

.posterImg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
}

.posterFallback {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--bg-elevated), var(--bg-surface));
  font-family: var(--font-display);
  font-size: 40px;
  color: var(--text-muted);
  z-index: 0;
}

/* + bookmark button on poster top-right */
.addBtn {
  position: absolute;
  top: 0;
  right: 0;
  width: 18px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(1px);
  border: none;
  border-radius: 1px;
  color: white;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  z-index: 3;
  transition: all var(--duration-fast);
}

.addBtn:hover {
  background: var(--accent-primary);
}

/* Info — right side content */
.info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  z-index: 1;
  padding-top: var(--space-1);
  min-width: 0;
}

/* Title block — positioned at top of info area */
.titleBlock {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: auto;
}

.year {
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 400;
  color: var(--text-secondary);
  line-height: 14px;
}

.cardTitle {
  font-family: var(--font-sans);
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  line-height: 25px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.genre {
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 400;
  color: var(--text-secondary);
  margin: 0;
  line-height: 14px;
}

/* Horizontal divider */
.divider {
  width: 100%;
  height: 1px;
  background: var(--border-subtle);
  margin: var(--space-2) 0 20px 0;
}

/* Revenue row — $22.5B  ▲ +2k this quarter [POSITIVE] */
.revenueRow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2) 12px;
  min-width: 0;
}

.revenue {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.currencySign {
  font-family: 'Bebas Neue', var(--font-display);
  font-size: 40px;
  font-weight: 700;
  color: var(--text-muted);
  line-height: 33px;
}

.revenueAmount {
  font-family: 'Bebas Neue', var(--font-display);
  font-size: 40px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 33px;
}

/* Share of platform revenue — the real figure that replaced the placeholder trend */
.shareBlock {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.shareValue {
  font-family: var(--font-sans);
  font-size: var(--text-base);
  font-weight: var(--weight-bold);
  color: var(--accent-secondary-text);
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}

.shareLabel {
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  color: var(--text-muted);
  line-height: 1.1;
}

/* Trend section — ▲ +2k this quarter / POSITIVE badge */
.trendSection {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.trendLine {
  display: flex;
  align-items: center;
  gap: 4px;
}

.trendIcon {
  color: #05c56b;
  font-size: 12px;
  line-height: 1;
}

.trendUp {
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 700;
  color: #05c56b;
  line-height: 15px;
}

.trendText {
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 700;
  color: var(--text-muted);
  line-height: 15px;
}

/* POSITIVE badge — 8px Axiforma Bold */
.trendBadge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  height: 14px;
  border: 1px solid var(--border-default);
  border-radius: 2px;
  font-family: var(--font-sans);
  font-size: var(--text-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0;
  color: var(--text-primary);
  background: transparent;
  line-height: 10px;
}
